import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Card,
  CardContent, Typography, Box, Chip, Avatar, CircularProgress, TextField,
  Divider, Stack, Alert,
} from '@mui/material';
import {
  PersonAdd, FlightTakeoff, DirectionsRun, CheckCircle, Groups,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { loadExecutiveWorkload, doAssignTrip, doBulkAssign, doRemoveTripAssignment } from '../../../reduxcomponents/slices/tripManagementSlice';

const WorkloadCard = ({ executive, selected, onSelect }) => {
  const isAvailable = executive.running < 5;
  return (
    <Card
      variant="outlined"
      onClick={() => onSelect(executive._id)}
      sx={{
        cursor: 'pointer',
        border: '2px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'primary.50' : 'background.paper',
        transition: '0.15s',
        '&:hover': { borderColor: 'primary.light', boxShadow: 2 },
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={1.5} mb={1}>
          <Avatar sx={{ bgcolor: selected ? 'primary.main' : 'grey.300', width: 36, height: 36 }}>
            {executive.fullName?.[0] || '?'}
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={600} noWrap>{executive.fullName}</Typography>
            <Chip
              label={isAvailable ? 'Available' : 'Busy'}
              color={isAvailable ? 'success' : 'warning'}
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: 10 }}
            />
          </Box>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Box textAlign="center">
            <Typography variant="h6" color="primary.main" lineHeight={1}>{executive.total_trips}</Typography>
            <Typography variant="caption" color="text.secondary">Total</Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box textAlign="center">
            <Typography variant="h6" color="success.main" lineHeight={1}>{executive.running}</Typography>
            <Typography variant="caption" color="text.secondary">Running</Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box textAlign="center">
            <Typography variant="h6" color="info.main" lineHeight={1}>{executive.upcoming}</Typography>
            <Typography variant="caption" color="text.secondary">Upcoming</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const TripAssignmentDialog = ({ open, onClose, trip, tripIds, onSuccess }) => {
  const dispatch = useDispatch();
  const { executiveWorkload, loading } = useSelector(s => s.tripManagement);
  const isBulk = Array.isArray(tripIds) && tripIds.length > 0;

  const [selectedExecutives, setSelectedExecutives] = useState([]);
  const [initialExecutives, setInitialExecutives] = useState([]);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      dispatch(loadExecutiveWorkload());
      let currentAssignees = [];
      if (!isBulk && trip && trip.allocation) {
        currentAssignees = trip.allocation.map(a => a.user_id?._id || a.user_id).filter(Boolean);
      }
      setInitialExecutives(currentAssignees);
      setSelectedExecutives(currentAssignees);
      setReason('');
      setError('');
    }
  }, [open, dispatch, trip, isBulk]);

  const handleToggleSelect = (execId) => {
    setSelectedExecutives(prev => 
      prev.includes(execId) ? prev.filter(id => id !== execId) : [...prev, execId]
    );
  };

  const handleAssign = async () => {
    if (selectedExecutives.length === 0 && initialExecutives.length === 0) { 
      setError('Please select at least one executive.'); 
      return; 
    }
    setError('');

    try {
      if (isBulk) {
        if (selectedExecutives.length === 0) {
          setError('Please select at least one executive for bulk assignment.');
          return;
        }
        await dispatch(doBulkAssign({ trip_ids: tripIds, executive_ids: selectedExecutives })).unwrap();
      } else {
        const added = selectedExecutives.filter(id => !initialExecutives.includes(id));
        const removed = initialExecutives.filter(id => !selectedExecutives.includes(id));
        
        const promises = [];
        if (added.length > 0) {
          promises.push(dispatch(doAssignTrip({ id: trip._id, data: { executive_ids: added, reason } })).unwrap());
        }
        if (removed.length > 0) {
          promises.push(dispatch(doRemoveTripAssignment({ id: trip._id, executive_ids: removed })).unwrap());
        }
        
        if (promises.length > 0) {
          await Promise.all(promises);
        }
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err || 'Assignment failed');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PersonAdd color="primary" />
          <Typography variant="h6">
            {isBulk ? `Assign ${tripIds.length} Trips` : 'Assign Trip'} to Executive(s)
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="subtitle2" color="text.secondary" mb={2}>
          Select operation executive(s) based on their current workload:
        </Typography>

        {loading.workload ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={1.5}>
            {executiveWorkload.map(exec => (
              <Grid item xs={12} sm={6} md={4} key={exec._id}>
                <WorkloadCard
                  executive={exec}
                  selected={selectedExecutives.includes(exec._id)}
                  onSelect={handleToggleSelect}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {!isBulk && (
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Assignment Reason (optional)"
            value={reason}
            onChange={e => setReason(e.target.value)}
            sx={{ mt: 2 }}
            size="small"
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={loading.action}
          startIcon={loading.action ? <CircularProgress size={16} /> : <PersonAdd />}
        >
          {isBulk ? 'Bulk Assign' : 'Save Assignments'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TripAssignmentDialog;
